import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircle, ArrowRight, BarChart3, Building2, CalendarDays, CheckCircle2, Info, Mail, Phone, Settings, Sparkles, User, UserCircle } from '../components/icons';
import { useCallback, useEffect, useMemo, useRef, useState, type ComponentType, type ReactNode } from 'react';
import Badge from '../components/Badge';
import Button from '../components/Button';
import Card from '../components/Card';
import Modal from '../components/Modal';
import { crmService } from '../services/crmService';
import { useToast } from '../store/toastContext';
import { cn } from '../utils/cn';
import type { EmpresaKanban, KanbanData, KanbanStatus, PessoaKanban } from '../types';

type VisibleKanbanStatus = 'Captadas' | 'Lista qualificada' | 'Contato iniciado' | 'Retornaram';

const columns: VisibleKanbanStatus[] = ['Captadas', 'Lista qualificada', 'Contato iniciado', 'Retornaram'];
const Presence = AnimatePresence as ComponentType<{ children?: ReactNode }>;

const emptyBoard = (): Record<KanbanStatus, EmpresaKanban[]> => ({
  Captadas: [],
  'Lista qualificada': [],
  'Em análise': [],
  'Contato iniciado': [],
  Retornaram: []
});

const formatDateTimePtBr = (value: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('pt-BR');
};

const statusVariant = (value: unknown): 'success' | 'warning' | 'neutral' | 'info' => {
  const normalized = String(value ?? '').toLowerCase();
  if (normalized === 'verificado' || normalized === 'true') return 'success';
  if (normalized === 'nao_verificado' || normalized === 'false') return 'warning';
  return 'neutral';
};

const printable = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

type DispatchChannel = 'whatsapp' | 'email';

type DispatchMessageView = {
  id: string;
  channel: DispatchChannel;
  text: string;
  createdAt: string | null;
};

type ReceivedMessageView = {
  id: string;
  text: string;
  createdAt: string | null;
};

type ConversationItem = {
  id: string;
  direction: 'outgoing' | 'incoming';
  channel: DispatchChannel | 'recebida';
  text: string;
  createdAt: string | null;
};

const sanitizeDispatchText = (value: unknown): string => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed || trimmed === '.') return '';
  return trimmed;
};

const normalizeDispatchMessages = (items: unknown[]): DispatchMessageView[] => {
  const normalized: DispatchMessageView[] = [];

  items.forEach((item, index) => {
    if (!item || typeof item !== 'object') return;
    const source = item as Record<string, unknown>;
    const idBase = typeof source.id === 'string' && source.id.trim() ? source.id.trim() : `dispatch_${index + 1}`;
    const createdAt = typeof source.created_at === 'string' && source.created_at.trim() ? source.created_at.trim() : null;

    const whatsappText = sanitizeDispatchText(source.mensagem_whatsapp ?? source.mensagem);
    if (whatsappText) {
      normalized.push({
        id: `${idBase}_whatsapp`,
        channel: 'whatsapp',
        text: whatsappText,
        createdAt
      });
    }

    const emailText = sanitizeDispatchText(source.email_enviado ?? source.email);
    if (emailText) {
      normalized.push({
        id: `${idBase}_email`,
        channel: 'email',
        text: emailText,
        createdAt
      });
    }
  });

  return normalized.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
};

const getDispatchCounts = (items: DispatchMessageView[]) =>
  items.reduce(
    (acc, item) => {
      if (item.channel === 'whatsapp') acc.whatsapp += 1;
      if (item.channel === 'email') acc.email += 1;
      return acc;
    },
    { whatsapp: 0, email: 0 }
  );

const normalizeReceivedMessages = (items: unknown[]): ReceivedMessageView[] => {
  const normalized: ReceivedMessageView[] = [];

  items.forEach((item, index) => {
    if (typeof item === 'string') {
      const text = item.trim();
      if (!text || text === '.') return;
      normalized.push({
        id: `received_text_${index + 1}`,
        text,
        createdAt: null
      });
      return;
    }

    if (!item || typeof item !== 'object') return;
    const source = item as Record<string, unknown>;
    const id = typeof source.id === 'string' && source.id.trim() ? source.id.trim() : `received_${index + 1}`;
    const createdAt = typeof source.created_at === 'string' && source.created_at.trim() ? source.created_at.trim() : null;
    const textCandidates = [source.mensagem, source.message, source.texto, source.text, source.resposta];

    for (const candidate of textCandidates) {
      const text = sanitizeDispatchText(candidate);
      if (!text) continue;
      normalized.push({ id, text, createdAt });
      return;
    }
  });

  return normalized.sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''));
};

const buildConversationTimeline = (dispatched: DispatchMessageView[], received: ReceivedMessageView[]): ConversationItem[] => {
  const outgoing: ConversationItem[] = dispatched.map((item) => ({
    id: `out_${item.id}`,
    direction: 'outgoing',
    channel: item.channel,
    text: item.text,
    createdAt: item.createdAt
  }));

  const incoming: ConversationItem[] = received.map((item) => ({
    id: `in_${item.id}`,
    direction: 'incoming',
    channel: 'recebida',
    text: item.text,
    createdAt: item.createdAt
  }));

  return [...outgoing, ...incoming].sort((a, b) => {
    const aTime = a.createdAt ? new Date(a.createdAt).getTime() : Number.MAX_SAFE_INTEGER;
    const bTime = b.createdAt ? new Date(b.createdAt).getTime() : Number.MAX_SAFE_INTEGER;
    if (aTime === bTime) return a.id.localeCompare(b.id);
    return aTime - bTime;
  });
};

const cloneBoard = (board: Record<KanbanStatus, EmpresaKanban[]>): Record<KanbanStatus, EmpresaKanban[]> => ({
  Captadas: [...board.Captadas],
  'Lista qualificada': [...board['Lista qualificada']],
  'Em análise': [...board['Em análise']],
  'Contato iniciado': [...board['Contato iniciado']],
  Retornaram: [...board.Retornaram]
});

export default function KanbanPage() {
  const { pushToast } = useToast();
  const [data, setData] = useState<KanbanData>({ totalEmpresas: 0, board: emptyBoard() });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOrder, setSortOrder] = useState<'recentes' | 'antigas'>('recentes');
  const [selectedEmpresa, setSelectedEmpresa] = useState<EmpresaKanban | null>(null);
  const [selectedPessoa, setSelectedPessoa] = useState<PessoaKanban | null>(null);
  const [deletingEmpresaId, setDeletingEmpresaId] = useState<string | null>(null);
  const [enrichingEmpresaId, setEnrichingEmpresaId] = useState<string | null>(null);
  const [dragging, setDragging] = useState<{ id: string; from: KanbanStatus } | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<KanbanStatus | null>(null);
  const dragRef = useRef<{ id: string; from: KanbanStatus; startX: number; startY: number; moved: boolean } | null>(null);
  const dragOverColumnRef = useRef<KanbanStatus | null>(null);
  const moveRef = useRef<(id: string, from: KanbanStatus, to: KanbanStatus) => Promise<void>>();

  const load = () =>
    crmService
      .getKanbanData()
      .then(setData)
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const move = useCallback(async (id: string, from: KanbanStatus, to: KanbanStatus) => {
    if (from === to) return;
    const previousBoard = cloneBoard(data.board);

    setData((prev) => {
      const source = [...prev.board[from]];
      const target = [...prev.board[to]];
      const index = source.findIndex((item) => item.id === id);
      if (index < 0) return prev;
      const [item] = source.splice(index, 1);
      target.unshift(item);
      return {
        ...prev,
        board: {
          ...prev.board,
          [from]: source,
          [to]: target
        }
      };
    });

    try {
      await crmService.updateEmpresaKanban(id, to);
    } catch {
      setData((prev) => ({
        ...prev,
        board: previousBoard
      }));
    }
  }, [data.board]);

  useEffect(() => {
    moveRef.current = move;
  }, [move]);

  const handleDeleteSelectedEmpresa = useCallback(async () => {
    if (!selectedEmpresa) return;
    const empresaId = selectedEmpresa.id;
    const previousBoard = cloneBoard(data.board);
    const previousTotal = data.totalEmpresas;

    setDeletingEmpresaId(empresaId);
    setData((prev) => ({
      ...prev,
      totalEmpresas: Math.max(0, prev.totalEmpresas - 1),
      board: {
        Captadas: prev.board.Captadas.filter((item) => item.id !== empresaId),
        'Lista qualificada': prev.board['Lista qualificada'].filter((item) => item.id !== empresaId),
        'Em análise': prev.board['Em análise'].filter((item) => item.id !== empresaId),
        'Contato iniciado': prev.board['Contato iniciado'].filter((item) => item.id !== empresaId),
        Retornaram: prev.board.Retornaram.filter((item) => item.id !== empresaId)
      }
    }));
    setSelectedPessoa(null);
    setSelectedEmpresa(null);

    try {
      await crmService.deleteEmpresaKanban(empresaId);
      pushToast({ type: 'success', title: 'Empresa excluida com sucesso.' });
    } catch {
      setData((prev) => ({
        ...prev,
        totalEmpresas: previousTotal,
        board: previousBoard
      }));
      pushToast({ type: 'error', title: 'Nao foi possivel excluir a empresa.' });
    } finally {
      setDeletingEmpresaId(null);
    }
  }, [data.board, data.totalEmpresas, pushToast, selectedEmpresa]);

  const handleEnrichCompany = useCallback(
    async (empresa: EmpresaKanban) => {
      setEnrichingEmpresaId(empresa.id);
      try {
        await crmService.enrichEmpresaKanban(empresa.nome_empresa, 'Busque hotel em são paulo', '');
        await move(empresa.id, 'Captadas', 'Lista qualificada');
        pushToast({ type: 'success', title: `Enriquecimento disparado para ${empresa.nome_empresa}.` });
      } catch (error) {
        pushToast({ type: 'error', title: error instanceof Error ? error.message : 'Nao foi possivel enriquecer a empresa.' });
      } finally {
        setEnrichingEmpresaId(null);
      }
    },
    [move, pushToast]
  );

  const visibleBoard = useMemo(() => {
    const nextBoard = emptyBoard();
    const term = searchTerm.trim().toLowerCase();

    columns.forEach((col) => {
      const filtered = data.board[col].filter((item) => (term ? item.nome_empresa.toLowerCase().includes(term) : true));
      filtered.sort((a, b) => {
        const aTime = new Date(a.updated_at || a.created_at || 0).getTime();
        const bTime = new Date(b.updated_at || b.created_at || 0).getTime();
        return sortOrder === 'recentes' ? bTime - aTime : aTime - bTime;
      });
      nextBoard[col] = filtered;
    });

    return nextBoard;
  }, [data.board, searchTerm, sortOrder]);

  const selectedEmpresaDispatches = useMemo(() => normalizeDispatchMessages(selectedEmpresa?.mensagens_disparadas_empresa || []), [selectedEmpresa?.mensagens_disparadas_empresa]);
  const selectedPessoaDispatches = useMemo(() => normalizeDispatchMessages(selectedPessoa?.mensagens_disparadas || []), [selectedPessoa?.mensagens_disparadas]);
  const selectedEmpresaReceived = useMemo(() => normalizeReceivedMessages(selectedEmpresa?.sub_mensagens_empresa || []), [selectedEmpresa?.sub_mensagens_empresa]);
  const selectedPessoaReceived = useMemo(() => normalizeReceivedMessages(selectedPessoa?.sub_mensagens || []), [selectedPessoa?.sub_mensagens]);
  const selectedEmpresaDispatchCounts = useMemo(() => getDispatchCounts(selectedEmpresaDispatches), [selectedEmpresaDispatches]);
  const selectedPessoaDispatchCounts = useMemo(() => getDispatchCounts(selectedPessoaDispatches), [selectedPessoaDispatches]);
  const selectedEmpresaConversation = useMemo(
    () => buildConversationTimeline(selectedEmpresaDispatches, selectedEmpresaReceived),
    [selectedEmpresaDispatches, selectedEmpresaReceived]
  );
  const selectedPessoaConversation = useMemo(
    () => buildConversationTimeline(selectedPessoaDispatches, selectedPessoaReceived),
    [selectedPessoaDispatches, selectedPessoaReceived]
  );

  const getColumnAtPoint = (clientX: number, clientY: number): KanbanStatus | null => {
    const target = document.elementFromPoint(clientX, clientY);
    const colNode = target?.closest('[data-kanban-col]') as HTMLElement | null;
    const direct = (colNode?.dataset.kanbanCol as KanbanStatus | undefined) || null;
    if (direct) return direct;

    const columnsNodes = Array.from(document.querySelectorAll<HTMLElement>('[data-kanban-col]'));
    for (const node of columnsNodes) {
      const rect = node.getBoundingClientRect();
      if (clientX >= rect.left && clientX <= rect.right) {
        return (node.dataset.kanbanCol as KanbanStatus | undefined) || null;
      }
    }
    return null;
  };

  const clearDrag = () => {
    dragRef.current = null;
    dragOverColumnRef.current = null;
    setDragging(null);
    setDragOverColumn(null);
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
  };

  const handlePointerDownCard = (event: React.PointerEvent<HTMLElement>, item: EmpresaKanban, from: KanbanStatus) => {
    if (event.button !== 0) return;
    const target = event.target as HTMLElement | null;
    if (target?.closest('button, a, input, textarea, select, [data-no-drag="true"]')) return;
    event.preventDefault();
    dragRef.current = { id: item.id, from, startX: event.clientX, startY: event.clientY, moved: false };
    dragOverColumnRef.current = from;
    setDragging({ id: item.id, from });
    setDragOverColumn(from);
  };

  useEffect(() => {
    const onPointerMove = (event: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;

      const deltaX = Math.abs(event.clientX - drag.startX);
      const deltaY = Math.abs(event.clientY - drag.startY);
      if (!drag.moved && (deltaX > 4 || deltaY > 4)) {
        drag.moved = true;
      }
      if (!drag.moved) return;

      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'grabbing';

      const col = getColumnAtPoint(event.clientX, event.clientY);
      if (dragOverColumnRef.current !== col) {
        dragOverColumnRef.current = col;
        setDragOverColumn(col);
      }
    };

    const onPointerUp = (event: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return clearDrag();
      const releasedCol = getColumnAtPoint(event.clientX, event.clientY);
      const finalCol = releasedCol || dragOverColumnRef.current;
      if (drag.moved && finalCol && drag.from !== finalCol) {
        void moveRef.current?.(drag.id, drag.from, finalCol);
      }
      clearDrag();
    };

    const onWindowBlur = () => clearDrag();

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('blur', onWindowBlur);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('blur', onWindowBlur);
      clearDrag();
    };
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Pipeline comercial</p>
          <h1 className="mt-1 text-3xl font-black">Kanban</h1>
        </div>
        <div className="rounded-2xl border border-borderLight bg-cardLight px-4 py-2 text-sm shadow-sm dark:border-borderDark dark:bg-cardDark">
          <span className="font-semibold">Total empresas:</span> {data.totalEmpresas}
        </div>
      </div>

      <Card className="p-3">
        <div className="grid gap-3 md:grid-cols-[1fr,220px]">
          <input
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Buscar empresa pelo nome"
            className="h-10 rounded-lg border border-borderLight bg-white px-3 text-sm outline-none ring-primary/25 focus:ring-2 dark:border-borderDark dark:bg-slate-900"
          />
          <select
            value={sortOrder}
            onChange={(event) => setSortOrder(event.target.value as 'recentes' | 'antigas')}
            className="h-10 rounded-lg border border-borderLight bg-white px-3 text-sm outline-none ring-primary/25 focus:ring-2 dark:border-borderDark dark:bg-slate-900"
          >
            <option value="recentes">Mais recentes</option>
            <option value="antigas">Mais antigas</option>
          </select>
        </div>
      </Card>

      {loading ? (
        <div className="grid gap-4 overflow-x-hidden xl:grid-cols-4">
          {columns.map((col) => (
            <Card key={`loading-${col}`} className="flex max-h-[74vh] flex-col space-y-3 bg-gradient-to-b from-white/95 to-slate-50 dark:from-slate-900 dark:to-slate-950">
              <div className="flex items-center justify-between rounded-xl border border-borderLight/70 bg-white/70 px-3 py-2 dark:border-borderDark/70 dark:bg-slate-900/50">
                <p className="text-sm font-semibold">{col}</p>
                <span className="h-5 w-8 animate-pulse rounded-full bg-slate-200 dark:bg-slate-700" />
              </div>

              <div className="space-y-3 overflow-y-auto overflow-x-hidden pr-1">
                {Array.from({ length: 4 }).map((_, idx) => (
                  <div key={`${col}-skeleton-${idx}`} className="rounded-xl border border-borderLight p-3 shadow-sm dark:border-borderDark">
                    <div className="mb-2 h-4 w-3/4 animate-pulse rounded bg-slate-200 dark:bg-slate-700" />
                    <div className="space-y-2">
                      <div className="h-3 w-2/3 animate-pulse rounded bg-slate-100 dark:bg-slate-800" />
                      <div className="h-3 w-5/6 animate-pulse rounded bg-slate-100 dark:bg-slate-800" />
                      <div className="h-3 w-1/2 animate-pulse rounded bg-slate-100 dark:bg-slate-800" />
                    </div>
                    <div className="mt-3 h-7 w-24 animate-pulse rounded bg-slate-100 dark:bg-slate-800" />
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      ) : null}

      {!loading ? (
      <div className="grid gap-4 overflow-x-hidden xl:grid-cols-4">
        {columns.map((col) => (
          <Card
            key={col}
            className={cn(
              'flex max-h-[74vh] flex-col space-y-3 bg-gradient-to-b from-white/95 to-slate-50 transition-colors dark:from-slate-900 dark:to-slate-950',
              dragOverColumn === col && 'ring-2 ring-primary/35'
            )}
          >
            <div className="flex items-center justify-between rounded-xl border border-borderLight/70 bg-white/70 px-3 py-2 dark:border-borderDark/70 dark:bg-slate-900/50">
              <p className="text-sm font-semibold">{col}</p>
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs dark:bg-slate-800">{visibleBoard[col].length}</span>
            </div>

            <div
              className="space-y-3 overflow-y-auto overflow-x-hidden pr-1"
              data-kanban-col={col}
            >
              <Presence>
                {visibleBoard[col].map((item, index) => (
                  <motion.div
                    key={`${col}_${item.id}`}
                    initial={{ opacity: 0, y: 12, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -6, scale: 0.97 }}
                    transition={{ duration: 0.18, delay: index * 0.02 }}
                  >
                    <div
                      data-company-card="true"
                      className={cn(
                        'touch-none select-none rounded-xl border border-borderLight p-3 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md dark:border-borderDark cursor-grab active:cursor-grabbing',
                        dragging?.id === item.id && 'opacity-70'
                      )}
                      onPointerDown={(event) => handlePointerDownCard(event, item, col)}
                    >
                      <div className="w-full min-w-0 text-left">
                        <div className="mb-2 flex items-start justify-between gap-2">
                          <p className="font-semibold leading-tight">{item.nome_empresa}</p>
                          <motion.span
                            whileHover={{ scale: 1.08, rotate: -3 }}
                            className="rounded-lg bg-primary/10 p-1.5 text-primary dark:bg-cta/20 dark:text-cta"
                          >
                            <Building2 className="h-3.5 w-3.5" />
                          </motion.span>
                        </div>

                        <div className="space-y-1.5 text-xs text-slate-600 dark:text-slate-300">
                          <p className="flex items-start gap-1.5 break-words">
                            <Phone className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                            {item.telefone}
                          </p>
                          <p className="flex items-start gap-1.5 break-words">
                            <Building2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                            {item.endereco}
                          </p>
                          <p className="flex items-center gap-1.5">
                            <BarChart3 className="h-3.5 w-3.5 shrink-0 text-amber-500" />
                            Avaliacao Google: {item.rating.toFixed(1)} · {item.reviews} reviews
                          </p>
                          <p className="flex items-center gap-1.5">
                            <UserCircle className="h-3.5 w-3.5 shrink-0" />
                            {item.pessoas.length} pessoa(s)
                          </p>
                        </div>
                      </div>

                    <div className="mt-3 flex items-center justify-between gap-2 border-t border-borderLight/70 pt-2 dark:border-borderDark/70">
                      <p className="text-[11px] text-slate-500">Criado: {formatDateTimePtBr(item.created_at)}</p>
                      {col === 'Captadas' ? (
                        <Button
                          type="button"
                          variant="outline"
                          className="h-8 shrink-0 whitespace-nowrap rounded-lg border-emerald-300 bg-gradient-to-r from-emerald-500 to-teal-500 px-3 text-[11px] font-bold text-white shadow-sm shadow-emerald-500/30 hover:brightness-110 dark:border-emerald-600"
                          iconLeft={<Sparkles className="h-3.5 w-3.5" />}
                          data-no-drag="true"
                          onMouseDown={(event) => event.stopPropagation()}
                          onClick={() => void handleEnrichCompany(item)}
                          disabled={enrichingEmpresaId !== null}
                        >
                          {enrichingEmpresaId === item.id ? 'Enriquecendo...' : 'Enriquecer'}
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          variant="outline"
                          className="h-7 shrink-0 whitespace-nowrap rounded-md px-2 text-[11px]"
                          iconLeft={<Info className="h-3.5 w-3.5" />}
                          data-no-drag="true"
                          onMouseDown={(event) => event.stopPropagation()}
                          onClick={() => setSelectedEmpresa(item)}
                        >
                          Informacoes
                        </Button>
                      )}
                    </div>
                    </div>
                  </motion.div>
                ))}
              </Presence>
            </div>
          </Card>
        ))}
      </div>
      ) : null}

      <Modal
        open={!!selectedEmpresa}
        onClose={() => {
          setSelectedEmpresa(null);
          setSelectedPessoa(null);
        }}
        title={selectedEmpresa ? `Empresa · ${selectedEmpresa.nome_empresa}` : undefined}
        fullscreen
        className="max-w-6xl"
      >
        {selectedEmpresa ? (
          <div className="grid gap-4 xl:grid-cols-[360px_1fr]">
            <aside className="space-y-4">
              <div className="rounded-2xl border border-borderLight bg-white p-4 shadow-sm dark:border-borderDark dark:bg-slate-950">
                <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Conta</p>
                <h3 className="mt-1 text-lg font-bold">{selectedEmpresa.nome_empresa}</h3>
                <div className="mt-3 space-y-2 text-sm text-slate-600 dark:text-slate-300">
                  <p className="flex items-center gap-2"><Phone className="h-4 w-4" />{selectedEmpresa.telefone}</p>
                  <p className="flex items-center gap-2"><Mail className="h-4 w-4" />{selectedEmpresa.email || '-'}</p>
                  <p className="flex items-center gap-2"><Building2 className="h-4 w-4" />{selectedEmpresa.endereco}</p>
                  <p className="flex items-center gap-2"><ArrowRight className="h-4 w-4" />{selectedEmpresa.website}</p>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Badge variant="neutral">Google {selectedEmpresa.rating.toFixed(1)} · {selectedEmpresa.reviews}</Badge>
                  <Badge variant="neutral">Atualizado: {formatDateTimePtBr(selectedEmpresa.updated_at)}</Badge>
                </div>
              </div>

              <div className="rounded-2xl border border-borderLight bg-white p-4 shadow-sm dark:border-borderDark dark:bg-slate-950">
                <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Pessoas ({selectedEmpresa.pessoas.length})</p>
                <div className="max-h-[42vh] space-y-2 overflow-y-auto pr-1">
                  {selectedEmpresa.pessoas.map((pessoa) => (
                    <button
                      key={pessoa.id}
                      type="button"
                      className="w-full rounded-xl border border-borderLight bg-slate-50 p-3 text-left transition hover:border-primary/40 hover:bg-primary/5 dark:border-borderDark dark:bg-slate-900"
                      onClick={() => setSelectedPessoa(pessoa)}
                    >
                      <p className="text-sm font-semibold">{pessoa.nome}</p>
                      <p className="text-xs text-slate-500">{pessoa.cargo || '-'}</p>
                      <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">{pessoa.email || '-'}</p>
                    </button>
                  ))}
                  {!selectedEmpresa.pessoas.length ? <p className="text-sm text-slate-500">Sem pessoas para esta empresa.</p> : null}
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                className="h-10 w-full rounded-xl border-red-300 text-red-600 hover:bg-red-50 dark:border-red-800/70 dark:text-red-300 dark:hover:bg-red-950/40"
                iconLeft={<AlertCircle className="h-4 w-4" />}
                onClick={handleDeleteSelectedEmpresa}
                disabled={deletingEmpresaId === selectedEmpresa.id}
              >
                {deletingEmpresaId === selectedEmpresa.id ? 'Excluindo...' : 'Excluir empresa'}
              </Button>
            </aside>

            <section className="rounded-2xl border border-borderLight bg-white p-4 shadow-sm dark:border-borderDark dark:bg-slate-950">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2 border-b border-borderLight pb-3 dark:border-borderDark">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Conversa</p>
                  <p className="text-sm font-semibold">Timeline da empresa ({selectedEmpresaConversation.length})</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="success">WhatsApp: {selectedEmpresaDispatchCounts.whatsapp}</Badge>
                  <Badge variant="info">E-mail: {selectedEmpresaDispatchCounts.email}</Badge>
                  <Badge variant="warning">Recebidas: {selectedEmpresaReceived.length}</Badge>
                </div>
              </div>
              <div className="max-h-[72vh] space-y-3 overflow-y-auto rounded-xl bg-slate-100/70 p-3 dark:bg-slate-900/50">
                {!selectedEmpresaConversation.length ? <p className="text-xs text-slate-500">Sem mensagens para esta empresa.</p> : null}
                {selectedEmpresaConversation.map((item) => (
                  <div key={item.id} className={cn('flex', item.direction === 'outgoing' ? 'justify-end' : 'justify-start')}>
                    <article
                      className={cn(
                        'max-w-[82%] rounded-2xl border px-3 py-2 shadow-sm',
                        item.direction === 'outgoing'
                          ? 'border-primary/30 bg-primary/10 dark:border-primary/50 dark:bg-primary/20'
                          : 'border-amber-300/70 bg-amber-50 dark:border-amber-700/60 dark:bg-amber-950/25'
                      )}
                    >
                      <div className="mb-1 flex items-center justify-between gap-2">
                        <Badge variant={item.direction === 'outgoing' ? (item.channel === 'whatsapp' ? 'success' : 'info') : 'warning'}>
                          {item.direction === 'outgoing' ? (item.channel === 'whatsapp' ? 'WhatsApp enviado' : 'E-mail enviado') : 'Mensagem recebida'}
                        </Badge>
                        <span className="text-[11px] text-slate-500">{formatDateTimePtBr(item.createdAt)}</span>
                      </div>
                      <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-700 dark:text-slate-200">{item.text}</p>
                    </article>
                  </div>
                ))}
              </div>
            </section>
          </div>
        ) : null}
      </Modal>

      <Modal
        open={!!selectedPessoa}
        onClose={() => setSelectedPessoa(null)}
        title={selectedPessoa ? `Pessoa · ${selectedPessoa.nome}` : undefined}
        fullscreen
        className="max-w-5xl"
      >
        {selectedPessoa ? (
          <div className="grid gap-4 xl:grid-cols-[340px_1fr]">
            <aside className="space-y-4">
              <div className="rounded-2xl border border-borderLight bg-white p-4 shadow-sm dark:border-borderDark dark:bg-slate-950">
                <div className="flex items-center gap-3">
                  <div className="relative h-14 w-14 overflow-hidden rounded-full border border-borderLight bg-slate-100 dark:border-borderDark dark:bg-slate-800">
                    <div className="absolute inset-0 grid place-items-center text-slate-400">
                      <UserCircle className="h-7 w-7" />
                    </div>
                    {selectedPessoa.foto_linkedin ? (
                      <img
                        src={selectedPessoa.foto_linkedin}
                        alt={selectedPessoa.nome}
                        className="relative z-10 h-full w-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : null}
                  </div>
                  <div>
                    <p className="text-lg font-bold">{selectedPessoa.nome}</p>
                    <p className="text-xs text-slate-500">{selectedPessoa.cargo || '-'}</p>
                  </div>
                </div>
                <div className="mt-3 space-y-2 text-sm text-slate-600 dark:text-slate-300">
                  <p className="flex items-center gap-2"><Mail className="h-4 w-4" />{selectedPessoa.email || '-'}</p>
                  <p className="flex items-center gap-2"><Phone className="h-4 w-4" />{selectedPessoa.telefone || '-'}</p>
                  <p className="flex items-center gap-2"><Settings className="h-4 w-4" />{selectedPessoa.setor || '-'}</p>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Badge variant={statusVariant(selectedPessoa.status_email)}>Email: {printable(selectedPessoa.status_email)}</Badge>
                  <Badge variant={statusVariant(selectedPessoa.status_telefone)}>Telefone: {printable(selectedPessoa.status_telefone)}</Badge>
                </div>
              </div>

              <div className="rounded-2xl border border-borderLight bg-white p-4 shadow-sm dark:border-borderDark dark:bg-slate-950">
                <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">mensagem disparada por:</p>
                <p className="text-sm font-semibold">{selectedPessoa.nome}</p>
                <p className="mt-1 text-xs text-slate-500">Aqui voce consulta o que chegou de resposta desse contato.</p>
              </div>
            </aside>

            <section className="rounded-2xl border border-borderLight bg-white p-4 shadow-sm dark:border-borderDark dark:bg-slate-950">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2 border-b border-borderLight pb-3 dark:border-borderDark">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Conversa</p>
                  <p className="text-sm font-semibold">Timeline do contato ({selectedPessoaConversation.length})</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="success">WhatsApp: {selectedPessoaDispatchCounts.whatsapp}</Badge>
                  <Badge variant="info">E-mail: {selectedPessoaDispatchCounts.email}</Badge>
                  <Badge variant="warning">Recebidas: {selectedPessoaReceived.length}</Badge>
                </div>
              </div>
              <div className="max-h-[72vh] space-y-3 overflow-y-auto rounded-xl bg-slate-100/70 p-3 dark:bg-slate-900/50">
                {!selectedPessoaConversation.length ? <p className="text-xs text-slate-500">Sem mensagens para este contato.</p> : null}
                {selectedPessoaConversation.map((item) => (
                  <div key={item.id} className={cn('flex', item.direction === 'outgoing' ? 'justify-end' : 'justify-start')}>
                    <article
                      className={cn(
                        'max-w-[82%] rounded-2xl border px-3 py-2 shadow-sm',
                        item.direction === 'outgoing'
                          ? 'border-primary/30 bg-primary/10 dark:border-primary/50 dark:bg-primary/20'
                          : 'border-amber-300/70 bg-amber-50 dark:border-amber-700/60 dark:bg-amber-950/25'
                      )}
                    >
                      <div className="mb-1 flex items-center justify-between gap-2">
                        <Badge variant={item.direction === 'outgoing' ? (item.channel === 'whatsapp' ? 'success' : 'info') : 'warning'}>
                          {item.direction === 'outgoing' ? (item.channel === 'whatsapp' ? 'WhatsApp enviado' : 'E-mail enviado') : 'Mensagem recebida'}
                        </Badge>
                        <span className="text-[11px] text-slate-500">{formatDateTimePtBr(item.createdAt)}</span>
                      </div>
                      <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-700 dark:text-slate-200">{item.text}</p>
                    </article>
                  </div>
                ))}
              </div>
            </section>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
